import { List, useTable, DateField } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import {
  Table,
  Tag,
  Row,
  Col,
  Card,
  Space,
  Typography,
  Radio,
  Tooltip,
  Grid,
} from "antd";
import {
  StopOutlined,
  UnlockOutlined,
  UserSwitchOutlined,
  FlagOutlined,
  SafetyCertificateOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { AdminAuditLog, AuditAction } from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import { melColors } from "@/theme/tokens";

const { Text } = Typography;
const { useBreakpoint } = Grid;

type ActionFilter = "all" | AuditAction;

/** Configuração visual por ação (cor da tag + ícone + rótulo pt-BR). */
const ACTION_CFG: Record<
  AuditAction,
  { color: string; icon: React.ReactNode; label: string }
> = {
  ban: { color: "red", icon: <StopOutlined />, label: "Banimento" },
  unban: { color: "green", icon: <UnlockOutlined />, label: "Desbanimento" },
  impersonate: {
    color: "purple",
    icon: <UserSwitchOutlined />,
    label: "Impersonação",
  },
  flag: { color: "orange", icon: <FlagOutlined />, label: "Flag" },
};

/** Renderiza o `meta` (JSON arbitrário) como tags chave: valor legíveis. */
function MetaTags({ meta }: { meta: Record<string, unknown> }) {
  const entries = Object.entries(meta ?? {});
  if (!entries.length) return <Text type="secondary">—</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {entries.map(([k, v]) => (
        <Tag
          key={k}
          style={{
            borderRadius: 8,
            background: "rgba(6,182,212,0.06)",
            borderColor: melColors.glassBorder,
            fontSize: 12,
          }}
        >
          <Text type="secondary">{k}:</Text> {String(v)}
        </Tag>
      ))}
    </Space>
  );
}

/**
 * /admin/audit-log — LOG DE AUDITORIA ADMINISTRATIVA (tabela `admin_audit`).
 *
 * Como obtém os dados (MOCK MODE): `useTable` lista os eventos paginados e
 * ordenados (mais recentes primeiro) via dataProvider (FIXTURES["audit-log"]);
 * um `useList` com `pagination: { mode: "off" }` traz todos para os cards de
 * resumo por ação. O filtro de ação aplica `setFilters([{ field: "action" }])`
 * (server-like no mock). Com a API real (VITE_MOCK=false) o mesmo resource
 * "audit-log" passa a ler de GET /admin/audit-log (simple-rest) sem mudar a tela.
 */
export const AuditLogList: React.FC = () => {
  const screens = useBreakpoint();
  const [action, setAction] = useState<ActionFilter>("all");

  const { tableProps, setFilters } = useTable<AdminAuditLog>({
    syncWithLocation: true,
    sorters: { initial: [{ field: "created_at", order: "desc" }] },
    pagination: { pageSize: 10 },
  });

  // Todos os eventos (sem paginação) para os cards de resumo.
  const { data: allData, isLoading: loadingAll } = useList<AdminAuditLog>({
    resource: "audit-log",
    pagination: { mode: "off" },
  });
  const all = useMemo(() => allData?.data ?? [], [allData]);

  const summary = useMemo(() => {
    const counts: Record<AuditAction, number> = {
      ban: 0,
      unban: 0,
      impersonate: 0,
      flag: 0,
    };
    let last: string | undefined;
    for (const ev of all) {
      counts[ev.action] = (counts[ev.action] ?? 0) + 1;
      if (!last || ev.created_at > last) last = ev.created_at;
    }
    return { total: all.length, counts, last };
  }, [all]);

  const onActionChange = (next: ActionFilter) => {
    setAction(next);
    setFilters(
      next === "all" ? [] : [{ field: "action", operator: "eq", value: next }],
      "replace",
    );
  };

  return (
    <List
      title="Log de auditoria"
      headerProps={{
        subTitle: "Ações administrativas sobre contas e conteúdo",
      }}
      headerButtons={
        summary.last ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Último evento:{" "}
            <DateField value={summary.last} format="DD/MM/YYYY HH:mm" />
          </Text>
        ) : undefined
      }
    >
      {/* Cards de resumo por ação — agregados de todos os eventos */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8} lg={4}>
          <StatCard
            title="Eventos no total"
            value={summary.total}
            prefix={<AuditOutlined style={{ color: melColors.aqua600 }} />}
            loading={loadingAll}
            hint="trilha completa"
          />
        </Col>
        <Col xs={12} md={8} lg={5}>
          <StatCard
            title="Banimentos"
            value={summary.counts.ban}
            prefix={<StopOutlined style={{ color: melColors.error }} />}
            loading={loadingAll}
            hint={`${summary.counts.unban} desbanimento(s)`}
          />
        </Col>
        <Col xs={12} md={8} lg={5}>
          <StatCard
            title="Desbanimentos"
            value={summary.counts.unban}
            prefix={<UnlockOutlined style={{ color: melColors.success }} />}
            loading={loadingAll}
            hint="contas reativadas"
          />
        </Col>
        <Col xs={12} md={12} lg={5}>
          <StatCard
            title="Impersonações"
            value={summary.counts.impersonate}
            prefix={<UserSwitchOutlined style={{ color: "#8B5CF6" }} />}
            loading={loadingAll}
            hint="acessos a contas via suporte"
          />
        </Col>
        <Col xs={12} md={12} lg={5}>
          <StatCard
            title="Flags"
            value={summary.counts.flag}
            prefix={<FlagOutlined style={{ color: melColors.warning }} />}
            loading={loadingAll}
            hint="conteúdo sinalizado"
          />
        </Col>
      </Row>

      {/* Filtro por ação */}
      <Card className="mel-glass" bordered={false} style={{ marginBottom: 16 }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }} wrap>
          <Radio.Group
            value={action}
            onChange={(e) => onActionChange(e.target.value as ActionFilter)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="all">Todas</Radio.Button>
            <Radio.Button value="ban">Banimentos</Radio.Button>
            <Radio.Button value="unban">Desbanimentos</Radio.Button>
            <Radio.Button value="impersonate">Impersonações</Radio.Button>
            <Radio.Button value="flag">Flags</Radio.Button>
          </Radio.Group>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <SafetyCertificateOutlined style={{ marginRight: 4 }} />
            Trilha imutável de auditoria
          </Text>
        </Space>
      </Card>

      {/* Tabela de eventos — ligada ao dataProvider (paginada/filtrada) */}
      <Card className="mel-glass" bordered={false} title="Eventos">
        <Table
          {...tableProps}
          rowKey="id"
          scroll={{ x: 900 }}
          locale={{ emptyText: "Nenhum evento para o filtro atual." }}
        >
          <Table.Column<AdminAuditLog>
            title="Ação"
            dataIndex="action"
            width={160}
            sorter
            render={(a: AuditAction) => {
              const cfg = ACTION_CFG[a] ?? ACTION_CFG.flag;
              return (
                <Tag color={cfg.color} icon={cfg.icon} style={{ borderRadius: 8 }}>
                  {cfg.label}
                </Tag>
              );
            }}
          />
          <Table.Column<AdminAuditLog>
            title="Alvo"
            dataIndex="target_email"
            render={(_, row) => (
              <Space direction="vertical" size={0}>
                <Text strong>{row.target_email ?? "—"}</Text>
                {row.target_user_id && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <code>{row.target_user_id}</code>
                  </Text>
                )}
              </Space>
            )}
          />
          {screens.md && (
            <Table.Column<AdminAuditLog>
              title="Admin"
              dataIndex="admin_email"
              sorter
              render={(v: string) => (
                <Tooltip title="Autor da ação">
                  <Text>{v}</Text>
                </Tooltip>
              )}
            />
          )}
          <Table.Column<AdminAuditLog>
            title="Detalhes (meta)"
            dataIndex="meta"
            render={(meta: Record<string, unknown>) => <MetaTags meta={meta} />}
          />
          <Table.Column<AdminAuditLog>
            title="Data"
            dataIndex="created_at"
            width={160}
            sorter
            defaultSortOrder="descend"
            render={(v: string) => (
              <DateField value={v} format="DD/MM/YYYY HH:mm" />
            )}
          />
        </Table>
      </Card>
    </List>
  );
};
